import { AdTemplate } from '@/components/AdTemplate';
import { JwVideoVideo } from '@/components/JwVideo/Video';
import { Layout } from '@/components/Layout';
import { PageMeta } from '@/components/PageMeta';
import { RelatedArticleSlider } from '@/components/RelatedArticleSlider';
import { SchemaMarkup } from '@/components/SchemaMarkup';
import { VideoBaseContent } from '@/components/VideoBaseContent';
import { VideoSlider } from '@/components/VideoSlider';
import { Content, Data, Meta } from '@/types/content';
import { getCurrentBrand } from '@/utils/getCurrentBrand';
import { getVideoPagesJwPLayer } from '@/utils/getJwPlayer';
import { getUrl } from '@/utils/getUrl';
import { isProtectedUrl } from '@/utils/isProtectedUrl';
import { removeTrailingSlash } from '@/utils/removeTrailingSlash';
import { slugify } from '@/utils/slugify';
import { HOUR } from '@/utils/ttl';
import { withCacheControlHeaders } from '@/utils/withCacheControlHeaders';
import { withSerializationGuard } from '@/utils/withSerializationGuard';
import { HttpStatusCode } from 'axios';
import { HORIZONTAL_ASPECT_RATIO, VERTICAL_ASPECT_RATIO } from 'base/components/JwVideo/Article/constants';
import { VideoPlayerType } from 'base/components/JwVideo/Article/types/video';
import formatMinutes from 'base/components/JwVideo/Article/utils/formatMinutes';
import formatMetaFields from 'base/components/JwVideo/Article/utils/metaData';
import { loadAdsData } from 'lib/data';
import { VideoMediaPage, getVideoMediaPageData } from 'lib/data/video';
import { getVideoRelatedArticles } from 'lib/data/video/getVideoRelatedArticles';
import { mapArticlesDataToSlides } from 'lib/data/video/mapArticlesDataToSlides';
import { convertVideoTagsToTagProps, filterVideoTags } from 'lib/data/video/tags';
import { MediaItem, MediaPlaylist, VideoTag } from 'lib/data/video/types';
import { AdsMeta } from 'lib/labrador/types';
import { omitBy } from 'lodash-es';
import { GetServerSidePropsContext, NextPage } from 'next';
import { useRouter } from 'next/router';
import { useEffectOnce, useLocalStorage } from 'react-use';
import { isString, isUndefined } from 'typesafe-utils';

const brand = getCurrentBrand();

export async function getServerSideProps({ params, res }: GetServerSidePropsContext) {
  const { clientAdsData: adsData } = await loadAdsData();

  const { mediaId, title } = (params ?? {}) as { mediaId: string; title: string };

  const mediaResponse = await getVideoMediaPageData(mediaId);
  const { media, tagsList, relatedMedia } = mediaResponse?.data ?? {};

  if (mediaResponse?.status === 410) {
    res.statusCode = HttpStatusCode.Gone;
    return { props: { isGone: true } };
  }

  const playlist = media?.playlist?.[0];
  const isProtected = await isProtectedUrl(playlist?.link);

  if (isProtected || !playlist) {
    return { notFound: true };
  }

  if (slugify(playlist.title) !== title) {
    return {
      redirect: {
        destination: `/video/${slugify(playlist.title)}/${mediaId}`,
        permanent: true,
      },
    };
  }

  const mainTag = filterVideoTags(playlist.objectTags)[0];

  const relatedArticles = await getVideoRelatedArticles(
    [mainTag?.name].filter(isString),
    [playlist?.video_category1, playlist?.video_category2].filter(Boolean),
  );

  withCacheControlHeaders(res, 6 * HOUR);

  return withSerializationGuard({
    props: {
      media,
      mainTag,
      tagsList,
      relatedMedia,
      relatedArticles,
      adsData,
    },
  });
}
interface PageProps extends VideoMediaPage {
  mainTag?: VideoTag;
  relatedArticles: Data[];
  adsData?: AdsMeta;
}

const SingleVideoPage: NextPage<PageProps> = ({ media, mainTag, relatedMedia, tagsList, relatedArticles, adsData }) => {
  const [seenVideos, setSeenVideos] = useLocalStorage<string[]>('seenVideos', []);
  const playlist = (media as MediaItem).playlist[0];

  const { asPath } = useRouter();

  const onComplete = (player: any) => {
    player?.remove();
    const nextVideo = relatedMedia[0];
    if (nextVideo) {
      window.location.href = nextVideo.url;
    }
  };

  const prominentTag = (playlist.prominentTag || mainTag?.name || '').toLowerCase();
  const videoProminentTagLink = `/video/tag/${prominentTag}`;
  const articleProminentTagLink = slugify(prominentTag);

  const pageData: Content = {
    meta: {},
    type: 'video.other',
    children: [],
    data: omitBy(
      {
        titleSuffix: ` | ${brand.name}`,
        seoTitle: media?.seo_title || media?.title,
        domain: brand.host,
        uploadDate: playlist.pubdate,
        contentUrl: media?.file,
        embedUrl: playlist.link || removeTrailingSlash(getUrl(asPath)?.href || ''),
        duration: playlist.duration,
        mentions: playlist.mentions,
        genre: prominentTag,
        canonical: removeTrailingSlash(getUrl(asPath)?.href || ''),
        seoDescription: media?.seo_beskrivning || media?.description || '',
        card: 'summary',
        transcript: media?.transcriptionData?.map((fragment) => fragment.text.join(' ')).join(' ') || '',
        keywords: [playlist.video_category1, playlist.video_category2].filter(Boolean).join(' '),
        ogImageUrl: playlist.image || '',
      },
      isUndefined,
    ),
  };

  const videoData = getVideoData(playlist);

  useEffectOnce(() => {
    const timeout = setTimeout(
      () => setSeenVideos((prev) => Array.from(new Set([playlist.mediaid, ...(prev ?? [])].splice(0, 10)))),
      5000,
    );
    return () => clearTimeout(timeout);
  });

  const notSeenRelatedMedia = relatedMedia.filter((video) => !(seenVideos ?? []).includes(video.mediaId));

  return (
    <Layout
      content={
        <>
          <AdTemplate pageMeta={pageData?.meta as Meta} adsData={adsData} />
          <PageMeta pageData={pageData} />
          <SchemaMarkup pageData={pageData} />

          <VideoBaseContent
            label="Populärt:"
            tagsList={tagsList}
            breadcrumbs={[
              { label: 'Video start', url: '/video' },
              { label: prominentTag, url: `video/tag/${prominentTag}` },
              { label: media?.title || '' },
            ]}
            options={{
              $breadcrumbs: {
                $step: { className: 'last:hidden md:last:block' },
                $separator: { className: 'last-of-type:hidden md:last-of-type:block' },
              },
            }}
          >
            <JwVideoVideo
              title={playlist.title}
              description={playlist.description}
              tags={convertVideoTagsToTagProps(playlist.objectTags)}
              transcript={{
                transcription: media?.transcriptionData,
              }}
              prominentTag={prominentTag}
              prominentTagLink={{ href: videoProminentTagLink }}
              share="Dela"
              video={{
                hideNextVideoPopup: true,
                playerSrc: `https://cdn.jwplayer.com/libraries/${getVideoPagesJwPLayer()}.js`,
                videoData,
                durationMinutes: formatMinutes(playlist.duration) ?? undefined,
                disableLazyLoad: true,
                metadata: formatMetaFields({
                  title: playlist.title,
                  description: playlist.description,
                  duration: playlist.duration,
                  mediaID: playlist.mediaid,
                  thumbnail: playlist.image,
                  publishedAt: new Date(playlist.pubdate * 1000).toISOString(),
                }),
                options: {
                  $image: { hidden: true },
                  $exit: { onComplete, videoCampaigns: [] },
                  $header: { className: 'hidden' },
                },
              }}
              options={{
                variant: videoData?.aspectRatio === VERTICAL_ASPECT_RATIO ? 'vertical' : 'horizontal',
              }}
            />

            <VideoSlider title="Upptäck mer" url={videoProminentTagLink} slides={notSeenRelatedMedia} />
            <RelatedArticleSlider
              link={{ href: articleProminentTagLink }}
              slider={{ slides: mapArticlesDataToSlides(relatedArticles) }}
            />
          </VideoBaseContent>
        </>
      }
    />
  );
};

const getVideoData = (playlist: MediaPlaylist): VideoPlayerType => {
  const { height, width } = playlist.sources.find((source) => source.file.includes('video')) ?? {};
  const isVertical = height && width && height > width;

  return {
    title: playlist.title,
    description: playlist.description,
    videoId: playlist.mediaid,
    playerId: getVideoPagesJwPLayer(),
    thumbnail: playlist.image,
    mute: true,
    duration: playlist.duration,
    date: new Date(playlist.pubdate * 1000).toISOString(),
    publishedAt: new Date(playlist.pubdate * 1000).toISOString(),
    autostart: 'viewable',
    floating: {
      dismissible: true,
      mode: 'notVisible',
    },
    playlist,
    aspectRatio: isVertical ? VERTICAL_ASPECT_RATIO : HORIZONTAL_ASPECT_RATIO,
    tracks: playlist.tracks,
  };
};

export default SingleVideoPage;
